import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { generateFullBlog, generateBlogOutline, saveBlogDraftV2, handleShare, getBlogById } from "../../utils/blog";
import BlogHeaderImage from "./BlogHeaderImage";
import BlogTitle from "./BlogTitle";
import BlogIntroduction from "./BlogIntro";
import BlogTags from "./BlogTags";
import BlogContent from "./BlogContent";
import AuthorName from "./AuthorName";
import "./GenerateBlogLeft.css";
import "./BlogDetails.css";
import GenerateBlogLeft from "./GenerateBlogLeft";
import BlogOptimizeModal from "./BlogOptimizeModal";
import BlogDraftModal from "./BlogDraftModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BiEdit, BiLaptop, BiMobile, BiRedo } from "react-icons/bi";
import RegenerateModal from "../../components/Common/RegenerateModal";
import { regenerateBlogSection } from "../../utils/blog";
import axiosInstance from "../../utils/api";
import HtmlContentRenderer from "./HtmlContentRenderer";
import { translateContent } from "../../services/translationService";

// Add minimal CSS for spinner animation
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the style
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('blog-spinner-style');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = 'blog-spinner-style';
    style.textContent = spinnerStyle;
    document.head.appendChild(style);
  }
}

const BlogPlayground = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { payload, formData, blogOutline, finalBlog, blogId, newPayload } = location.state || {};

  // Use the full formData for display
  const form = formData || {};

  const [jsonContent, setJsonContent] = useState("");
  const [hasFirstChunk, setHasFirstChunk] = useState(false);
  const [isStreamingDone, setIsStreamingDone] = useState(false);
  const [parsedBlog, setParsedBlog] = useState(finalBlog || null);
  const [currentBlogId, setCurrentBlogId] = useState(blogId || null);
  const [sections, setSections] = useState(finalBlog?.sections || null);
  const [isHtmlFormat, setIsHtmlFormat] = useState(false);
  const [htmlContent, setHtmlContent] = useState(null);

  // Form state
  const [formState, setFormState] = useState({
    about: formData?.topic || formData?.about || "",
    tone: formData?.tone || "casual",
    format: formData?.format || newPayload?.format || "html",
    word_limit: formData?.word_limit || "short (200-600 words)",
    location: formData?.location || "",
    language: formData?.language || "english",
    further_details: formData?.further_details || "",
    brand_context: formData?.brand_context || {},
    semantic_clusters: formData?.semantic_clusters || [],
    exclude_topics: formData?.exclude_topics || "",
    cta_mode: formData?.cta_mode || "",
    cta_product_name: formData?.cta_product_name || "",
    cta_url: formData?.cta_url || "",
    faq_count: formData?.faq_count || "",
    blog_type: formData?.blog_type || "",
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [language, setLanguage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [showBlogDraftModal, setShowBlogDraftModal] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [allLocations, setAllLocations] = useState([]);

  // New state for translation
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState("");
  const [translationStream, setTranslationStream] = useState("");
  const [originalContent, setOriginalContent] = useState(null);


  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/listing?type=country');
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const locationOptions = response.data.data.map(item => ({
            value: item.name,
            label: item.name,
          }));
          setAllLocations(locationOptions);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    fetchLocations();
  }, []);

  // Cursor blinking effect during streaming
  useEffect(() => {
    if (!isStreaming) return;
    
    const interval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 500);
    
    return () => clearInterval(interval);
  }, [isStreaming]);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (streamingContentRef.current && jsonContent) {
      streamingContentRef.current.scrollTop = streamingContentRef.current.scrollHeight;
    }
  }, [jsonContent]);

  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };
  const [textColor, setTextColor] = useState("#000000");
  const [viewMode, setViewMode] = useState("desktop");
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [editSectionText, setEditSectionText] = useState("");
  const [hasInitialized, setHasInitialized] = useState(false);

  // State for regeneration modal
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [regenerateContext, setRegenerateContext] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [streamingStats, setStreamingStats] = useState({ startTime: null, charCount: 0, chunksReceived: 0 });
  const [cursorVisible, setCursorVisible] = useState(true);
  const streamingContentRef = useRef(null);
  
  // Error state management
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [parsingError, setParsingError] = useState(false);
  const [rawStreamedContent, setRawStreamedContent] = useState("");

  const handleDeleteSection = (index) => {
    const updatedSections = sections.filter((_, i) => i !== index);
    setSections(updatedSections);
  };

  // Retry function for failed blog generation
  const handleRetry = () => {
    setHasError(false);
    setErrorMessage("");
    setParsingError(false);
    setRawStreamedContent("");
    setJsonContent("");
    setHasFirstChunk(false);
    setIsStreamingDone(false);
    setParsedBlog(null);
    setIsStreaming(true);
    setStreamingStats({ startTime: null, charCount: 0, chunksReceived: 0 });
    
    const payloadToUse = newPayload || payload;
    if (payloadToUse) {
      let fullText = "";
      if (newPayload) {
        // New format
        generateFullBlog(
          newPayload.topic,
          newPayload.tone,
          newPayload.wordCountTarget,
          newPayload.location,
          newPayload.language,
          newPayload.semanticClusters || [],
          newPayload.brandContext || "",
          newPayload.excludeTopics || "",
          newPayload.ctaMode || "",
          newPayload.ctaProductName || "",
          newPayload.ctaUrl || "",
          newPayload.faqCount || "",
          newPayload.topicMapJson,
          newPayload.blogType || "",
          newPayload.sessionId,
          newPayload.agentId,
          newPayload.uid,
          newPayload.query || newPayload.topic,
          newPayload.format || "html",
          (chunk) => {
            if (!hasFirstChunk) {
              setHasFirstChunk(true);
              setStreamingStats(prev => ({ ...prev, startTime: Date.now() }));
            }
            setJsonContent((prev) => prev + chunk);
            setStreamingStats(prev => ({ 
              ...prev, 
              charCount: prev.charCount + chunk.length,
              chunksReceived: prev.chunksReceived + 1
            }));
            fullText += chunk;
          }
        )
          .then(() => {
            setIsStreamingDone(true);
            setIsStreaming(false);
            try {
              const firstBrace = fullText.indexOf("{");
              const lastBrace = fullText.lastIndexOf("}");
              if (firstBrace !== -1 && lastBrace !== -1) {
                const jsonStr = fullText.substring(firstBrace, lastBrace + 1);
                setParsedBlog(JSON.parse(jsonStr));
                setParsingError(false);
              } else {
                setParsedBlog(null);
                setParsingError(true);
                setRawStreamedContent(fullText);
              }
            } catch (e) {
              setParsedBlog(null);
              setParsingError(true);
              setRawStreamedContent(fullText);
              setErrorMessage("Failed to parse JSON response");
            }
          })
          .catch((error) => {
            setIsStreaming(false);
            setIsStreamingDone(true);
            setParsedBlog(null);
            setHasError(true);
            setErrorMessage(error.message || "Network error");
            toast.error("Failed to generate blog: " + (error.message || "Network error"));
          });
      } else if (payload) {
        // Old format
        generateFullBlog(
          payload.additional_data?.blog_about || formData?.topic || formData?.about || "",
          payload.additional_data?.tone_of_voice || formData?.tone || "",
          payload.additional_data?.blog_length || formData?.word_limit || "",
          payload.additional_data?.location_focus || formData?.location || "",
          payload.additional_data?.language || formData?.language || "",
          payload.additional_data?.semantic_clusters || formData?.semantic_clusters || [],
          payload.additional_data?.brand_context || formData?.brand_context || "",
          formData?.exclude_topics || "",
          formData?.cta_mode || "",
          formData?.cta_product_name || "",
          formData?.cta_url || "",
          formData?.faq_count || "",
          typeof blogOutline === 'string' ? blogOutline : JSON.stringify(blogOutline || {}),
          formData?.blog_type || "",
          payload.additional_data?.session_id || formData?.session_id || "",
          payload.additional_data?.agent_id || formData?.agent_id || "",
          payload.additional_data?.uid || formData?.uid || "",
          payload.additional_data?.blog_about || formData?.topic || formData?.about || "",
          payload.additional_data?.format || "html",
          (chunk) => {
            if (!hasFirstChunk) {
              setHasFirstChunk(true);
              setStreamingStats(prev => ({ ...prev, startTime: Date.now() }));
            }
            setJsonContent((prev) => prev + chunk);
            setStreamingStats(prev => ({ 
              ...prev, 
              charCount: prev.charCount + chunk.length,
              chunksReceived: prev.chunksReceived + 1
            }));
            fullText += chunk;
          }
        )
          .then(() => {
            setIsStreamingDone(true);
            setIsStreaming(false);
            try {
              const firstBrace = fullText.indexOf("{");
              const lastBrace = fullText.lastIndexOf("}");
              if (firstBrace !== -1 && lastBrace !== -1) {
                const jsonStr = fullText.substring(firstBrace, lastBrace + 1);
                setParsedBlog(JSON.parse(jsonStr));
                setParsingError(false);
              } else {
                setParsedBlog(null);
                setParsingError(true);
                setRawStreamedContent(fullText);
              }
            } catch (e) {
              setParsedBlog(null);
              setParsingError(true);
              setRawStreamedContent(fullText);
              setErrorMessage("Failed to parse JSON response");
            }
          })
          .catch((error) => {
            setIsStreaming(false);
            setIsStreamingDone(true);
            setParsedBlog(null);
            setHasError(true);
            setErrorMessage(error.message || "Network error");
            toast.error("Failed to generate blog: " + (error.message || "Network error"));
          });
      }
    }
  };

  // Function to format streaming content for better display
  const formatStreamingContent = (content) => {
    if (!content) return '';
    
    // Try to extract and format JSON content
    try {
      // Look for JSON structure in the streaming content
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const jsonStr = jsonMatch[0];
        const parsed = JSON.parse(jsonStr);
        
        // Format the JSON nicely
        return JSON.stringify(parsed, null, 2);
      }
    } catch (e) {
      // If parsing fails, return the raw content
    }
    
    // Return formatted raw content
    return content
      .replace(/\n/g, '\n')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Enhanced function to render different content types properly with comprehensive format handling
  const renderSectionContent = (content, sectionTitle) => {
    if (!content) return "Lorem ipsum dolor sit amet, consectetur adipiscing elit.";

    // Handle null/undefined content
    if (content === null || content === undefined) {
      return "No content available.";
    }

    // General content pattern analyzer for automatic format detection
    const analyzeContentPattern = (contentStr) => {
      const patterns = {
        // Numbered lists with bold text
        numberedBold: /\d+\.\s*\*\*.*?\*\*/g,
        // Bullet points with bold text
        bulletBold: /^-\s*\*\*.*?\*\*/gm,
        // Q&A format
        qaFormat: /^[Qq]:.*\n^[Aa]:/gm,
        // Table format (pipe-separated)
        tableFormat: /\|.*\|/g,
        // Markdown headers
        markdownHeaders: /^#{1,6}\s+.*$/gm,
        // Code blocks
        codeBlocks: /```[\s\S]*?```/g,
        // Links
        links: /\[.*?\]\(.*?\)/g,
        // Bold text
        boldText: /\*\*.*?\*\*/g,
        // Italic text
        italicText: /\*.*?\*/g,
        // Multiple paragraphs
        multipleParagraphs: /\n\s*\n/g
      };

      const detectedPatterns = {};
      for (const [patternName, pattern] of Object.entries(patterns)) {
        const matches = contentStr.match(pattern);
        detectedPatterns[patternName] = matches ? matches.length : 0;
      }

      return detectedPatterns;
    };

    // Handle FAQ sections
    if (sectionTitle && sectionTitle.toLowerCase().includes('frequently asked questions')) {
      if (Array.isArray(content)) {
        return (
          <div>
            {content.map((faq, index) => (
              <div key={index} style={{ marginBottom: 20, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ 
                    backgroundColor: '#E36250', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '28px', 
                    height: '28px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '14px', 
                    marginRight: '12px',
                    flexShrink: 0
                  }}>
                    Q
                  </span>
                  {faq.question}
                </div>
                <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '40px' }}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        );
      } else if (typeof content === 'string') {
        // Handle FAQ content that comes as a string
        // Parse different formats:
        // 1. "Q: Question\nA: Answer\n\nQ: Question2\nA: Answer2"
        // 2. Bullet point format with markdown like "- **Question**: Answer"
        
        // First try Q: A: format
        if (content.includes('Q:') && content.includes('A:')) {
          const faqBlocks = content.split(/\n\s*\n/).filter(block => block.trim());
          return (
            <div>
              {faqBlocks.map((block, index) => {
                const lines = block.split('\n').filter(line => line.trim());
                const questionLine = lines.find(line => line.startsWith('Q:') || line.startsWith('Q:'));
                const answerLine = lines.find(line => line.startsWith('A:') || line.startsWith('A:'));
                
                if (questionLine && answerLine) {
                  const question = questionLine.replace(/^Q:\s*/, '').trim();
                  const answer = answerLine.replace(/^A:\s*/, '').trim();
                  
                  return (
                    <div key={index} style={{ marginBottom: 20, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                      <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                        <span style={{ 
                          backgroundColor: '#E36250', 
                          color: 'white', 
                          borderRadius: '50%', 
                          width: '28px', 
                          height: '28px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: '14px', 
                          marginRight: '12px',
                          flexShrink: 0
                        }}>
                          Q
                        </span>
                        {question}
                      </div>
                      <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.6', paddingLeft: '40px' }}>
                        {answer}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }
        
        // Handle bullet point format with markdown like "- **Question**: Answer"
        if (content.includes('- **') && content.includes('**:')) {
          const lines = content.split('\n').filter(line => line.trim());
          return (
            <div>
              {lines.map((line, index) => {
                if (line.startsWith('- **') && line.includes('**:')) {
                  const match = line.match(/^-\s*\*\*(.*?)\*\*:\s*(.*)/);
                  if (match) {
                    const [, question, answer] = match;
                    return (
                      <div key={index} style={{ marginBottom: 20, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                          <span style={{ 
                            backgroundColor: '#E36250', 
                            color: 'white', 
                            borderRadius: '50%', 
                            width: '28px', 
                            height: '28px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '14px', 
                            marginRight: '12px',
                            flexShrink: 0
                          }}>
                            Q
                          </span>
                          {question}
                        </div>
                        <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.6', paddingLeft: '40px' }}>
                          {answer}
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              })}
            </div>
          );
        }
        
        // If no specific format is detected, render as plain text with proper formatting
        return (
          <div style={{ 
            fontSize: '14px', 
            color: '#4b5563', 
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap'
          }}>
            {content}
          </div>
        );
      }
    }

    // Handle object content (non-array objects)
    if (typeof content === 'object' && content !== null && !Array.isArray(content)) {
      const entries = Object.entries(content);
      
      return (
        <div>
          {entries.map(([key, value], index) => (
            <div key={index} style={{ 
              marginBottom: 16, 
              padding: '16px', 
              backgroundColor: '#f8fafc', 
              borderRadius: '8px', 
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                fontWeight: 600,
                fontSize: '16px',
                color: "#1f2937",
                marginBottom: 8,
                textTransform: "capitalize",
              }}>
                {key.replace(/_/g, ' ')}
              </div>
              <div>
                {typeof value === 'string' ? (
                  <p style={{
                    fontSize: '14px',
                    color: "#4b5563",
                    lineHeight: 1.6,
                    margin: 0,
                  }}>
                    {value}
                  </p>
                ) : Array.isArray(value) ? (
                  <div>
                    {value.map((item, idx) => (
                      <div key={idx} style={{ 
                        marginBottom: 8, 
                        padding: '8px', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '4px'
                      }}>
                        {typeof item === 'string' ? item : JSON.stringify(item)}
                      </div>
                    ))}
                  </div>
                ) : typeof value === 'object' ? (
                  <div style={{
                    fontSize: '12px',
                    color: "#6b7280",
                    fontFamily: "monospace",
                    backgroundColor: "#f3f4f6",
                    padding: "8px",
                    borderRadius: "4px",
                    overflow: "auto",
                  }}>
                    {JSON.stringify(value, null, 2)}
                  </div>
                ) : (
                  <span style={{
                    fontSize: '14px',
                    color: "#4b5563",
                  }}>
                    {String(value)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Handle Myths sections
    if (sectionTitle && sectionTitle.toLowerCase().includes('myths')) {
      if (typeof content === 'string') {
        // Parse numbered myths format like "1. **Does Shilajit Expire?**: Yes, Shilajit does..."
        const myths = content.split(/(?=\d+\.\s*\*\*)/);
        return (
          <div>
            {myths.map((myth, index) => {
              if (!myth.trim()) return null;
              const match = myth.match(/(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
              if (match) {
                const [, number, question, answer] = match;
                return (
                  <div key={index} style={{ 
                    marginBottom: 16, 
                    padding: '16px', 
                    backgroundColor: '#fef3f2', 
                    borderRadius: '8px', 
                    border: '1px solid #fecaca', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
                  }}>
                    <div style={{ 
                      fontWeight: 600, 
                      fontSize: '16px', 
                      color: '#dc2626', 
                      marginBottom: '8px', 
                      display: 'flex', 
                      alignItems: 'center' 
                    }}>
                      <span style={{ 
                        backgroundColor: '#dc2626', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '24px', 
                        height: '24px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '12px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {number}
                      </span>
                      {question}
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#4b5563', 
                      lineHeight: 1.6, 
                      paddingLeft: '36px' 
                    }}>
                      {answer.trim()}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      }
    }

    // Handle array content (like numbered lists)
    if (Array.isArray(content)) {
      // Check if this is a single string that should be processed as a whole
      if (content.length === 1 && typeof content[0] === 'string') {
        const singleString = content[0];
        // Check if it contains numbered FAQ format
        if (singleString.match(/\d+\.\s*\*\*.*?\*\*/)) {
          // Process as a single FAQ string
          const patterns = analyzeContentPattern(singleString);
          
          if (patterns.numberedBold > 0) {
            // Handle numbered lists with bold text (most common pattern)
            const items = singleString.split(/(?=\d+\.\s*\*\*)/);
            
            return (
              <div>
                {items.map((item, index) => {
                  if (!item.trim()) return null;
                  
                  // Try different patterns for this item
                  let question, answer, number;
                  
                  // Pattern 1: "1. **Question?**\n   Answer text" (indented)
                  const indentedPattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*\s*\n\s+(.*)/s);
                  if (indentedPattern) {
                    [, number, question, answer] = indentedPattern;
                  } else {
                    // Pattern 2: "1. **Question?**: Answer text" (inline)
                    const inlinePattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
                    if (inlinePattern) {
                      [, number, question, answer] = inlinePattern;
                    } else {
                      // Pattern 3: "1. **Question?**" (question only)
                      const questionOnlyPattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*/);
                      if (questionOnlyPattern) {
                        [, number, question] = questionOnlyPattern;
                        // Get answer from remaining lines
                        const lines = item.split('\n').filter(line => line.trim());
                        answer = lines.slice(1).map(line => line.trim()).join(' ').trim();
                      }
                    }
                  }
                  
                  if (question) {
                    return (
                      <div key={index} style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                          <span style={{ 
                            backgroundColor: '#E36250', 
                            color: 'white', 
                            borderRadius: '50%', 
                            width: '28px', 
                            height: '28px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '14px', 
                            marginRight: '12px',
                            flexShrink: 0
                          }}>
                            {number}
                          </span>
                          {question}
                        </div>
                        {answer && (
                          <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.6', paddingLeft: '40px' }}>
                            {answer}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  return null;
                })}
              </div>
            );
          }
        }
      }
      
      // Handle regular array items
      return (
        <div>
          {content.map((item, index) => {
            // Handle different item types in the array
            if (typeof item === 'string') {
              const itemText = item;
              // Check if it's a numbered list item with bold text
              const match = itemText.match(/^(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/);
              if (match) {
                const [, number, title, description] = match;
                return (
                  <div key={index} style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#0369a1', marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                      <span style={{ 
                        backgroundColor: '#0369a1', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '24px', 
                        height: '24px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '12px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {number}
                      </span>
                      {title}
                    </div>
                    <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '36px' }}>
                      {description}
                    </div>
                  </div>
                );
              }
              
              // Check if it's a numbered FAQ item with bold text and answer (no colon)
              // Format: "1. **Question?** Answer text"
              const faqMatch = itemText.match(/^(\d+)\.\s*\*\*(.*?)\*\*\s*(.*)/);
              if (faqMatch) {
                const [, number, question, answer] = faqMatch;
                return (
                  <div key={index} style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ 
                        backgroundColor: '#E36250', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '14px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {number}
                      </span>
                      {question}
                    </div>
                    {answer && (
                      <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.6', paddingLeft: '40px' }}>
                        {answer}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular string item
              return (
                <div key={index} style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                  {itemText}
                </div>
              );
            } else if (typeof item === 'object' && item !== null) {
              // Handle object items in arrays
              if (item.question && item.answer) {
                // FAQ format
                return (
                  <div key={index} style={{ marginBottom: 20, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ 
                        backgroundColor: '#E36250', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '14px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        Q
                      </span>
                      {item.question}
                    </div>
                    <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '40px' }}>
                      {item.answer}
                    </div>
                  </div>
                );
              } else {
                // Generic object - display as key-value pairs
                const entries = Object.entries(item);
                return (
                  <div key={index} style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    {entries.map(([key, value], idx) => (
                      <div key={idx} style={{ marginBottom: '8px' }}>
                        <strong style={{ color: '#0369a1' }}>{key.replace(/_/g, ' ')}: </strong>
                        <span>{typeof value === 'string' ? value : JSON.stringify(value)}</span>
                      </div>
                    ))}
                  </div>
                );
              }
            } else {
              // Handle other types (numbers, booleans, etc.)
              return (
                <div key={index} style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                  {String(item)}
                </div>
              );
            }
          })}
        </div>
      );
    }

    // Handle string content
    if (typeof content === 'string') {
      // Use pattern analysis to automatically detect the best rendering method
      const patterns = analyzeContentPattern(content);
      
      // Smart content rendering based on detected patterns
      if (patterns.numberedBold > 0) {
        // Handle numbered lists with bold text (most common pattern)
        const items = content.split(/(?=\d+\.\s*\*\*)/);
        
        return (
          <div>
            {items.map((item, index) => {
              if (!item.trim()) return null;
              
              // Try different patterns for this item
              let question, answer, number;
              
              // Pattern 1: "1. **Question?**\n   Answer text" (indented)
              const indentedPattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*\s*\n\s+(.*)/s);
              if (indentedPattern) {
                [, number, question, answer] = indentedPattern;
              } else {
                // Pattern 2: "1. **Question?**: Answer text" (inline)
                const inlinePattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
                if (inlinePattern) {
                  [, number, question, answer] = inlinePattern;
                } else {
                  // Pattern 3: "1. **Question?**" (question only)
                  const questionOnlyPattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*/);
                  if (questionOnlyPattern) {
                    [, number, question] = questionOnlyPattern;
                    // Get answer from remaining lines
                    const lines = item.split('\n').filter(line => line.trim());
                    answer = lines.slice(1).map(line => line.trim()).join(' ').trim();
                  }
                }
              }
              
              if (question) {
                return (
                  <div key={index} style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ 
                        backgroundColor: '#E36250', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '14px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {number}
                      </span>
                      {question}
                    </div>
                    {answer && (
                      <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '40px' }}>
                        {answer}
                      </div>
                    )}
                  </div>
                );
              }
              
              return null;
            })}
          </div>
        );
      }
      
      // If no patterns detected, try direct FAQ format detection
      if (content.includes('**') && content.match(/\d+\./)) {
        console.log('Direct FAQ format detected!');
        console.log('Content:', content);
        
        // Handle the exact format from DB: "1. **Question?**\n   Answer\n\n2. **Question2?**\n   Answer2"
        // Split by double newlines to separate FAQ items
        const faqItems = content.split(/\n\s*\n/).filter(item => item.trim());
        console.log('FAQ items split by double newlines:', faqItems);
        
        return (
          <div>
            {faqItems.map((item, index) => {
              if (!item.trim()) return null;
              
              // Split each FAQ item into lines
              const lines = item.split('\n').filter(line => line.trim());
              if (lines.length === 0) return null;
              
              // First line should contain the question
              const questionLine = lines[0];
              const questionMatch = questionLine.match(/^(\d+)\.\s*\*\*(.*?)\*\*/);
              
              if (questionMatch) {
                const [, number, question] = questionMatch;
                
                // Get the answer from remaining lines (remove leading spaces)
                const answerLines = lines.slice(1).map(line => line.trim()).filter(line => line);
                const answer = answerLines.join(' ');
                
                console.log('FAQ item parsed:', { number, question, answer });
                
                return (
                  <div key={index} style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ 
                        backgroundColor: '#E36250', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '14px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {number}
                      </span>
                      {question}
                    </div>
                    {answer && (
                      <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.6', paddingLeft: '40px' }}>
                        {answer}
                      </div>
                    )}
                  </div>
                );
              }
              
              return null;
            })}
          </div>
        );
      }
      
      // Handle other detected patterns
      if (patterns.bulletBold > 0) {
        // Handle bullet points with bold headings
        const lines = content.split('\n').filter(line => line.trim());
        return (
          <div>
            {lines.map((line, index) => {
              if (line.startsWith('- **') && line.includes('**:')) {
                const match = line.match(/^-\s*\*\*(.*?)\*\*:\s*(.*)/);
                if (match) {
                  const [, heading, content] = match;
                  return (
                    <div key={index} style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: 600, fontSize: '16px', color: '#0369a1', marginBottom: '8px' }}>
                        {heading}
                      </div>
                      <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6 }}>
                        {content}
                      </div>
                    </div>
                  );
                }
              }
              // Handle regular lines
              if (line.trim() && !line.startsWith('- **')) {
                return (
                  <div key={index} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    {line.trim()}
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      }
      
      if (patterns.qaFormat > 0) {
        // Handle Q&A format
        const qaBlocks = content.split(/\n\s*\n/).filter(block => block.trim());
        return (
          <div>
            {qaBlocks.map((block, index) => {
              const lines = block.split('\n').filter(line => line.trim());
              const questionLine = lines.find(line => line.startsWith('Q:') || line.startsWith('q:'));
              const answerLine = lines.find(line => line.startsWith('A:') || line.startsWith('a:'));
              
              if (questionLine && answerLine) {
                const question = questionLine.replace(/^[Qq]:/, '').trim();
                const answer = answerLine.replace(/^[Aa]:/, '').trim();
                
                return (
                  <div key={index} style={{ marginBottom: 20, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ 
                        backgroundColor: '#E36250', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '14px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        Q
                      </span>
                      {question}
                    </div>
                    <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.6', paddingLeft: '40px' }}>
                      {answer}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      }
      
      if (patterns.tableFormat > 0) {
        // Handle table format
        const lines = content.split('\n').filter(line => line.trim());
        const tableLines = lines.filter(line => line.includes('|'));
        
        if (tableLines.length >= 2) {
          const headers = tableLines[0].split('|').map(h => h.trim()).filter(h => h);
          const dataRows = tableLines.slice(1).filter(line => 
            line.includes('|') && line.split('|').filter(cell => cell.trim()).length >= 2
          );
          
          return (
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <thead>
                  <tr>
                    {headers.map((header, index) => (
                      <th key={index} style={{
                        padding: '12px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#f3f4f6',
                        fontWeight: 600,
                        color: '#1f2937',
                        textAlign: 'left'
                      }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, rowIndex) => {
                    const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
                    return (
                      <tr key={rowIndex} style={{
                        backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        {cells.map((cell, cellIndex) => (
                          <td key={cellIndex} style={{
                            padding: '12px',
                            borderRight: cellIndex < cells.length - 1 ? '1px solid #e5e7eb' : 'none'
                          }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
      }
      
      // General pattern detection for various numbered list formats
      // This will catch most numbered list patterns automatically
      if (content.match(/^\d+\.\s*\*\*.*?\*\*/m)) {
        // Split by numbered items
        const items = content.split(/(?=\d+\.\s*\*\*)/);
        
        return (
          <div>
            {items.map((item, index) => {
              if (!item.trim()) return null;
              
              // Try different patterns for this item
              let question, answer, number;
              
              // Pattern 1: "1. **Question?**\n   Answer text" (indented)
              const indentedPattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*\s*\n\s+(.*)/s);
              if (indentedPattern) {
                [, number, question, answer] = indentedPattern;
              } else {
                // Pattern 2: "1. **Question?**: Answer text" (inline)
                const inlinePattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
                if (inlinePattern) {
                  [, number, question, answer] = inlinePattern;
                } else {
                  // Pattern 3: "1. **Question?**" (question only)
                  const questionOnlyPattern = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*/);
                  if (questionOnlyPattern) {
                    [, number, question] = questionOnlyPattern;
                    // Get answer from remaining lines
                    const lines = item.split('\n').filter(line => line.trim());
                    answer = lines.slice(1).map(line => line.trim()).join(' ').trim();
                  }
                }
              }
              
              if (question) {
                return (
                  <div key={index} style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ 
                        backgroundColor: '#E36250', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '14px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {number}
                      </span>
                      {question}
                    </div>
                    {answer && (
                      <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '40px' }}>
                        {answer}
                      </div>
                    )}
                  </div>
                );
              }
              
              return null;
            })}
          </div>
        );
      }
      
      // Check if content contains numbered list with bold text like "1. **Title**: Description"
      if (content.match(/\d+\.\s*\*\*.*?\*\*:\s*/)) {
        const items = content.split(/(?=\d+\.\s*\*\*)/);
        return (
          <div>
            {items.map((item, index) => {
              if (!item.trim()) return null;
              const match = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
              if (match) {
                const [, number, title, description] = match;
                return (
                  <div key={index} style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#0369a1', marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                      <span style={{ 
                        backgroundColor: '#0369a1', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '24px', 
                        height: '24px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '12px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {number}
                      </span>
                      {title}
                    </div>
                    <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '36px' }}>
                      {description.trim()}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      }
      
      // Check if content contains numbered list with bold questions followed by indented answers
      // Format: "1. **Question?**\n   Answer text"
      if (content.match(/\d+\.\s*\*\*.*?\*\*\s*\n\s+/)) {
        const items = content.split(/(?=\d+\.\s*\*\*)/);
        return (
          <div>
            {items.map((item, index) => {
              if (!item.trim()) return null;
              
              // Split by lines to separate question and answer
              const lines = item.split('\n').filter(line => line.trim());
              if (lines.length === 0) return null;
              
              // Extract question from first line
              const questionMatch = lines[0].match(/^(\d+)\.\s*\*\*(.*?)\*\*/);
              if (!questionMatch) return null;
              
              const [, number, question] = questionMatch;
              
              // Get answer from remaining lines (remove leading spaces)
              const answer = lines.slice(1).map(line => line.trim()).join(' ').trim();
              
              return (
                <div key={index} style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ 
                      backgroundColor: '#E36250', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: '28px', 
                      height: '28px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '14px', 
                      marginRight: '12px',
                      flexShrink: 0
                    }}>
                      {number}
                    </span>
                    {question}
                  </div>
                  {answer && (
                    <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '40px' }}>
                      {answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }
      
      // Check if content contains bullet points with bold headings like "- **Heading**: Content"
      if (content.match(/^-\s*\*\*.*?\*\*:\s*/m)) {
        const lines = content.split('\n').filter(line => line.trim());
        return (
          <div>
            {lines.map((line, index) => {
              if (line.startsWith('- **') && line.includes('**:')) {
                const match = line.match(/^-\s*\*\*(.*?)\*\*:\s*(.*)/);
                if (match) {
                  const [, heading, content] = match;
                  return (
                    <div key={index} style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: 600, fontSize: '16px', color: '#0369a1', marginBottom: '8px' }}>
                        {heading}
                      </div>
                      <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6 }}>
                        {content}
                      </div>
                    </div>
                  );
                }
              }
              // Handle regular lines
              if (line.trim() && !line.startsWith('- **')) {
                return (
                  <div key={index} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    {line.trim()}
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      }
      
      // Check if content contains table format (pipe-separated values)
      if (content.includes('|') && content.split('\n').filter(line => line.includes('|')).length >= 2) {
        const lines = content.split('\n').filter(line => line.trim());
        const tableLines = lines.filter(line => line.includes('|'));
        
        if (tableLines.length >= 2) {
          const headers = tableLines[0].split('|').map(h => h.trim()).filter(h => h);
          const dataRows = tableLines.slice(1).filter(line => 
            line.includes('|') && line.split('|').filter(cell => cell.trim()).length >= 2
          );
          
          return (
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                <thead>
                  <tr>
                    {headers.map((header, index) => (
                      <th key={index} style={{
                        padding: '12px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#f3f4f6',
                        fontWeight: 600,
                        color: '#1f2937',
                        textAlign: 'left'
                      }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, rowIndex) => {
                    const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
                    return (
                      <tr key={rowIndex} style={{
                        backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        {cells.map((cell, cellIndex) => (
                          <td key={cellIndex} style={{
                            padding: '12px',
                            borderRight: cellIndex < cells.length - 1 ? '1px solid #e5e7eb' : 'none'
                          }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
      }
      
      // If content has multiple paragraphs, render them properly
      if (content.includes('\n\n') || content.split('\n').filter(line => line.trim()).length > 1) {
        const paragraphs = content.split('\n\n').filter(p => p.trim());
        return (
          <div>
            {paragraphs.map((paragraph, index) => (
              <div key={index} style={{ 
                marginBottom: '16px', 
                padding: '16px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#4b5563', 
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {paragraph.trim()}
                </div>
              </div>
            ))}
          </div>
        );
      }
      
      // Default string rendering
      return (
        <div style={{ 
          fontSize: '14px', 
          color: '#4b5563', 
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          padding: '16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          {content}
        </div>
      );
    }

    // Fallback for other types
    if (typeof content === 'number') {
      return (
        <div style={{ 
          fontSize: '16px', 
          color: '#1f2937', 
          fontWeight: 600,
          padding: '12px',
          backgroundColor: '#f0f9ff',
          borderRadius: '6px',
          border: '1px solid #bae6fd'
        }}>
          {content}
        </div>
      );
    }
    
    if (typeof content === 'boolean') {
      return (
        <div style={{ 
          fontSize: '14px', 
          color: content ? '#059669' : '#dc2626',
          padding: '8px 12px',
          backgroundColor: content ? '#d1fae5' : '#fee2e2',
          borderRadius: '6px',
          border: `1px solid ${content ? '#a7f3d0' : '#fecaca'}`
        }}>
          {content ? 'True' : 'False'}
        </div>
      );
    }
    
    // For any other type, convert to string and display
    return (
      <div style={{ 
        fontSize: '14px', 
        color: '#6b7280',
        padding: '12px',
        backgroundColor: '#f3f4f6',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        fontFamily: 'monospace'
      }}>
        {String(content || 'No content available')}
      </div>
    );
  };

  // Utility to clean up keys (remove spaces and colons) and flatten nested 'sections'
  function normalizeBlogData(obj) {
    if (Array.isArray(obj)) {
      // Flatten nested arrays
      return obj.flatMap(normalizeBlogData);
    } else if (obj && typeof obj === 'object') {
      // Clean keys and recursively normalize values
      const cleaned = Object.entries(obj).reduce((acc, [key, value]) => {
        const cleanKey = key.replace(/\s+|:/g, '');
        acc[cleanKey] = normalizeBlogData(value);
        return acc;
      }, {});
      // If this object has a 'sections' key, flatten its value
      if (cleaned.sections) {
        cleaned.sections = normalizeBlogData(cleaned.sections);
      }
      return cleaned;
    }
    return obj;
  }
  function flattenSections(sections) {
    if (!sections) return [];
    if (!Array.isArray(sections)) return [sections];
    return sections.flatMap(section => {
      if (section.sections) {
        return flattenSections(section.sections);
      }
      return [section];
    });
  }

  // Handle form changes
  const handleChange = (e) => {
    if (e.target.name === "brand_context") {
      // Handle brand_context as an object
      setFormState({ ...formState, brand_context: e.target.value });
    } else {
      // Handle regular string fields
      setFormState({ ...formState, [e.target.name]: e.target.value });
    }
  };

  useEffect(() => {
    if (selectedLocation) {
      const newLocation = selectedLocation.label || (typeof selectedLocation === 'string' ? selectedLocation : formState.location);
      setFormState(prev => ({ ...prev, location: newLocation }));
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (language) {
      const newLanguage = language.label || (typeof language === 'string' ? language : formState.language);
      setFormState(prev => ({ ...prev, language: newLanguage }));
    }
  }, [language]);

  // Clean streaming response data
  const cleanStreamingResponse = (rawData) => {
    // Remove all "data: " prefixes and combine the content
    const lines = rawData.split('\n');
    let cleanedContent = '';
    
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        // Remove "data: " prefix and add to cleaned content
        cleanedContent += line.substring(6);
      } else if (line.trim() !== '') {
        // If line doesn't start with "data: " but isn't empty, add it
        cleanedContent += line;
      }
    }
    
    return cleanedContent;
  };

  // Revert to original content
  const revertToOriginal = () => {
    if (originalContent) {
      if (originalContent.sections) {
        setSections(originalContent.sections);
        setIsHtmlFormat(false);
        setHtmlContent(null);
      } else if (originalContent.htmlContent) {
        setHtmlContent(originalContent.htmlContent);
        setIsHtmlFormat(true);
        setSections(null);
        
        // Update parsedBlog if it exists
        if (originalContent.parsedBlog) {
          setParsedBlog(originalContent.parsedBlog);
        }
      } else if (originalContent.parsedBlog) {
        setParsedBlog(originalContent.parsedBlog);
        // Determine format from parsedBlog
        const format = originalContent.parsedBlog?.metadata?.format || formData?.format || "html";
        if (format === "html" && originalContent.parsedBlog?.blog?.content_html) {
          setIsHtmlFormat(true);
          setHtmlContent(originalContent.parsedBlog.blog.content_html);
          setSections(null);
        } else {
          setIsHtmlFormat(false);
          setHtmlContent(null);
          if (originalContent.parsedBlog?.sections) {
            setSections(originalContent.parsedBlog.sections);
          }
        }
      }
      setFormState(originalContent.formState);
      setTranslatedContent("");
      setTranslationStream("");
      toast.info("Reverted to original content");
    }
  };
  // Handle optimize (generate outline)

  const onTranslate = async () => {
    // Check if we have content to translate - support both HTML and sections format
    const hasSections = sections && sections.length > 0;
    const hasHtmlContent = htmlContent && htmlContent.trim().length > 0;
    const hasParsedBlog = parsedBlog && (parsedBlog.blog || parsedBlog.sections);
    
    if (!hasSections && !hasHtmlContent && !hasParsedBlog) {
      toast.error("Please generate blog content first before translating");
      return;
    }

    const targetLanguage = formState.language || language?.label || "English";
    const targetRegion = formState.location || selectedLocation?.label || "Global";

    if (!targetLanguage || targetLanguage === "English") {
      toast.error("Please select a target language for translation");
      return;
    }

    setIsTranslating(true);
    setTranslatedContent("");
    setTranslationStream("");
    
    // Store the original content before translation
    if (!originalContent) {
      setOriginalContent({
        sections: hasSections ? [...sections] : null,
        htmlContent: hasHtmlContent ? htmlContent : null,
        parsedBlog: hasParsedBlog ? parsedBlog : null,
        isHtmlFormat: isHtmlFormat,
        formState: { ...formState }
      });
    }
    
    try {
      // Prepare the JSON data to send for translation
      let jsonData;
      
      if (hasSections) {
        // Use sections format
        jsonData = {
          sections: sections.map(section => ({
            title: section.title || section.section_title || "",
            content: section.content || "",
            suggested_image_or_infographic: section.suggested_image_or_infographic || ""
          }))
        };
      } else if (hasHtmlContent || (parsedBlog?.blog?.content_html)) {
        // Convert HTML format to sections for translation
        const htmlToTranslate = htmlContent || parsedBlog?.blog?.content_html || "";
        jsonData = {
          content: htmlToTranslate
        };
      } else if (parsedBlog?.sections) {
        // Use parsedBlog sections
        jsonData = {
          sections: parsedBlog.sections.map(section => ({
            title: section.title || section.section_title || "",
            content: section.content || "",
            suggested_image_or_infographic: section.suggested_image_or_infographic || ""
          }))
        };
      } else {
        throw new Error("No valid content format found for translation");
      }

      // Use the translation service
      await translateContent({
        jsonData,
        sourceLanguage: "English",
        targetLanguage,
        targetRegion,
        sessionId: formData?.session_id || null,
        onChunk: (chunk, accumulatedData) => {
          // Always update translation stream for UI feedback
          setTranslationStream(accumulatedData);
          
          // If chunk is a parsed object, try to update content incrementally
          if (typeof chunk === 'object' && chunk !== null) {
            // Handle sections format
            if (chunk.sections && Array.isArray(chunk.sections)) {
              setSections(prevSections => {
                if (!prevSections || prevSections.length === 0) {
                  return chunk.sections;
                }
                // Merge translated sections with existing sections
                return prevSections.map((prevSection, index) => {
                  const translatedSection = chunk.sections[index];
                  if (translatedSection) {
                    return {
                      ...prevSection,
                      title: translatedSection.title || prevSection.title,
                      content: translatedSection.content || prevSection.content,
                      suggested_image_or_infographic: translatedSection.suggested_image_or_infographic || prevSection.suggested_image_or_infographic
                    };
                  }
                  return prevSection;
                });
              });
              setIsHtmlFormat(false);
            }
            // Handle HTML content format
            else if (chunk.content || chunk.html) {
              const translatedHtml = chunk.content || chunk.html;
              setHtmlContent(translatedHtml);
              setIsHtmlFormat(true);
              setSections(null);
              
              // Update parsedBlog if it exists
              if (parsedBlog) {
                setParsedBlog(prev => ({
                  ...prev,
                  blog: {
                    ...prev?.blog,
                    content_html: translatedHtml
                  }
                }));
              }
            }
          }
        },
        onComplete: (finalData) => {
          try {
            // Parse the final translated data
            let parsedTranslation = finalData;
            
            // If it's a string, try to parse it
            if (typeof finalData === 'string') {
              // Clean the data first
              const cleanedData = cleanStreamingResponse(finalData);
              try {
                parsedTranslation = JSON.parse(cleanedData);
              } catch (e) {
                // If parsing fails, might be HTML content directly
                parsedTranslation = { content: cleanedData };
              }
            }
            
            // Handle different response formats
            if (parsedTranslation && parsedTranslation.sections && Array.isArray(parsedTranslation.sections)) {
              // Sections format - update sections
              setSections(parsedTranslation.sections);
              setIsHtmlFormat(false);
              setHtmlContent(null);
              
              // Update parsedBlog if it exists to keep it in sync with translated sections
              if (parsedBlog) {
                setParsedBlog(prev => ({
                  ...prev,
                  sections: parsedTranslation.sections // Update with translated sections
                }));
              }
              
              // Store the complete translated content for reference
              setTranslatedContent(parsedTranslation);
              toast.success("Translation completed successfully! Your blog content has been replaced with the translated version.");
            } else if (parsedTranslation && (parsedTranslation.content || parsedTranslation.html)) {
              // HTML format - update htmlContent
              const translatedHtml = parsedTranslation.content || parsedTranslation.html;
              setHtmlContent(translatedHtml);
              setIsHtmlFormat(true);
              setSections(null);
              
              // Update parsedBlog if it exists
              if (parsedBlog) {
                setParsedBlog(prev => ({
                  ...prev,
                  blog: {
                    ...prev?.blog,
                    content_html: translatedHtml
                  }
                }));
              }
              
              setTranslatedContent(parsedTranslation);
              toast.success("Translation completed successfully! Your blog content has been replaced with the translated version.");
            } else if (parsedTranslation && typeof parsedTranslation === 'object') {
              // Generic object - try to extract content
              if (parsedTranslation.sections) {
                setSections(parsedTranslation.sections);
                setIsHtmlFormat(false);
                setHtmlContent(null);
                
                // Update parsedBlog if it exists to keep it in sync
                if (parsedBlog) {
                  setParsedBlog(prev => ({
                    ...prev,
                    sections: parsedTranslation.sections
                  }));
                }
              } else if (parsedTranslation.content || parsedTranslation.html) {
                const translatedHtml = parsedTranslation.content || parsedTranslation.html;
                setHtmlContent(translatedHtml);
                setIsHtmlFormat(true);
                setSections(null);
                
                // Update parsedBlog if it exists
                if (parsedBlog) {
                  setParsedBlog(prev => ({
                    ...prev,
                    blog: {
                      ...prev?.blog,
                      content_html: translatedHtml
                    }
                  }));
                }
              }
              
              setTranslatedContent(parsedTranslation);
              toast.success("Translation completed successfully!");
            } else {
              throw new Error("Invalid translation response format");
            }
          } catch (parseError) {
            console.error("Error parsing translation response:", parseError);
            // If parsing fails, store the raw data
            setTranslatedContent({ raw: finalData, error: "Failed to parse response" });
            toast.warning("Translation completed but failed to parse response format");
          } finally {
            // Turn off loader when streaming completes
            setIsTranslating(false);
          }
        },
        onError: (error) => {
          console.error("Translation error:", error);
          toast.error("Translation failed: " + (error.message || "Unknown error"));
          // Turn off loader on error
          setIsTranslating(false);
        }
      });
      
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Translation failed: " + (error.message || "Unknown error"));
    } finally {
      setIsTranslating(false);
    }
  };
  
  const handleOptimize = async (isTranslate=false) => {
    // Validate required fields
    if (!formState.about?.trim()) {
      toast.error("Please enter what the blog is about");
      return;
    }
    if (!formState.tone?.trim()) {
      toast.error("Please select a tone of voice");
      return;
    }
    if (!formState.word_limit?.trim()) {
      toast.error("Please select the blog length");
      return;
    }

    setLoading(true);
    setJsonContent(""); 
    setStreamingContent("");
    setHasFirstChunk(false); 
    setIsStreaming(true);
    setIsStreamingDone(false); 
    setParsedBlog(null);
    setSections(null); 
    let fullText = "";
    try {
      // Use current sections state if available, otherwise fall back to blogOutline
      const currentTopicMap = sections || blogOutline;
      const topicMapJson = typeof currentTopicMap === 'string' 
        ? currentTopicMap 
        : JSON.stringify(currentTopicMap || {});

      await generateFullBlog(
        formState.topic || formState.about || "",
        formState.tone || "",
        formState.word_limit || "",
        formState.location || selectedLocation?.label || "",
        formState.language || language?.label || "",
        formState.semantic_clusters || [],
        formState.brand_context || "",
        formState.exclude_topics || "",
        formState.cta_mode || "",
        formState.cta_product_name || "",
        formState.cta_url || "",
        formState.faq_count || "",
        topicMapJson,
        formState.blog_type || formData?.blog_type || "",
        formData.session_id || "",
        formData.agent_id || "",
        formData.uid || "",
        formState.topic || formState.about || "",
        formState.format || newPayload?.format || formData?.format || "html",
        (chunk) => {
          if (!hasFirstChunk) setHasFirstChunk(true);
          setJsonContent((prev) => prev + chunk); 
          fullText += chunk;
        }
      );

      setIsStreamingDone(true);
      try {
        const firstBrace = fullText.indexOf("{");
        const lastBrace = fullText.lastIndexOf("}");
        let jsonStr = fullText;
        if (firstBrace !== -1 && lastBrace !== -1) {
          jsonStr = fullText.substring(firstBrace, lastBrace + 1);
        }
        setParsedBlog(JSON.parse(jsonStr));
      } catch (e) {
        console.error("Error parsing blog content after optimize:", e);
        setParsedBlog(null);
        setParsingError(true);
        setRawStreamedContent(fullText);
        setErrorMessage("Failed to parse the optimized blog content");
        toast.error("Failed to parse the generated blog content.");
      }
    } catch (err) {
      console.error("Error in handleOptimize (generateFullBlog):", err);
      toast.error("Failed to generate blog: " + (err.message || "Unknown error"));
      setParsedBlog(null); 
      setIsStreamingDone(true); 
    } finally {
      setLoading(false);
      setIsStreaming(false); 
    }
  };

  const handleOpenRegenerateModal = (sectionIdx, currentText) => {
    setRegenerateContext({ sectionIdx, currentText });
    setIsRegenerateModalOpen(true);
  };

  const handleRegenerateSubmit = async (promptData) => {

    if (!regenerateContext) {
      return;
    }
    setIsRegenerating(true);
    const { sectionIdx, currentText } = regenerateContext;

    try {
      // Assuming parsedBlog has a structure like { sections: [{ title: "", content: "" }] }
      // You might need to adjust this based on the actual structure of parsedBlog and sections
      const sectionToRegenerate = sections[sectionIdx];

      // Handle both simple string (reason) and custom prompt object
      let customPrompt;
      if (typeof promptData === 'string') {
        customPrompt = promptData;
      } else if (promptData && typeof promptData === 'object') {
        // If it's a custom prompt object, format it appropriately
        customPrompt = `Role: ${promptData.role || ''}, Goal: ${promptData.goal || ''}, Constraints: ${promptData.constraints || ''}, Reason: ${promptData.reason || ''}`;
      } else {
        customPrompt = 'Please regenerate this section';
      }

      let fullText = "";
      await regenerateBlogSection(
        {
          agentId: formData.agent_id,
          sessionId: formData.session_id,
          uid: formData.uid || "",
          blogType: formData.blog_type || formState.blog_type || "blog",
          sectionContent: currentText,
          customPrompt: customPrompt,
          sectionName: sectionToRegenerate?.section_title || sectionToRegenerate?.title || `Section ${sectionIdx + 1}`,
        },
        (chunk) => {
          fullText += chunk;
        }
      );

      // Parse the result to extract the new content

      let newContent = "";
      try {
        // Clean the response text first
        let cleanedText = fullText.trim();
        
        // Remove any SSE prefixes if present
        if (cleanedText.startsWith('data: ')) {
          cleanedText = cleanedText.replace(/^data: /, '');
        }
        
        // Try to find JSON within the response
        const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          cleanedText = jsonMatch[0];
        }
        
        
        // Try to parse as JSON first
        const parsedResult = JSON.parse(cleanedText);
        
        if (parsedResult && parsedResult.new_content) {
          newContent = parsedResult.new_content;
        } else if (parsedResult && parsedResult.content) {
          newContent = parsedResult.content;
        } else if (parsedResult && parsedResult.semantic_clusters && parsedResult.semantic_clusters[0] && parsedResult.semantic_clusters[0].new_content) {
          newContent = parsedResult.semantic_clusters[0].new_content;
        } else if (parsedResult && parsedResult.semantic_clusters && parsedResult.semantic_clusters[0] && parsedResult.semantic_clusters[0].content) {
          newContent = parsedResult.semantic_clusters[0].content;
        } else if (parsedResult && parsedResult.sections && parsedResult.sections[0] && parsedResult.sections[0].content) {
          newContent = parsedResult.sections[0].content;
        } else if (parsedResult && parsedResult.sections && parsedResult.sections[0] && parsedResult.sections[0].new_content) {
          newContent = parsedResult.sections[0].new_content;
        } else if (typeof parsedResult === 'string') {
          newContent = parsedResult;
        } else {
          // If no structured content found, use the cleaned text
          newContent = cleanedText;
        }
      } catch (e) {
        // If not valid JSON, use the raw text
        newContent = fullText.trim();
      }
      
      // Ensure we have some content
      if (!newContent || newContent.trim() === '') {
        console.warn('No content extracted, using fallback');
        newContent = 'Content regenerated successfully.';
      }

      // Update the sections state with the new content
      const updatedSections = [...sections];
      updatedSections[sectionIdx] = {
        ...updatedSections[sectionIdx],
        content: newContent,
        // Add a timestamp to ensure the object reference changes
        lastUpdated: Date.now()
      };
      
      // Use functional update to ensure state change is detected
      setSections(prevSections => {
        return updatedSections;
      });
      
      // Force a re-render by updating a trigger
      setUpdateTrigger(prev => {
        const newTrigger = prev + 1;
        return newTrigger;
      });

      // Also update parsedBlog if it exists
      if (parsedBlog) {
        const updatedParsedBlog = { ...parsedBlog };
        // If parsedBlog has sections array, update the corresponding section
        if (Array.isArray(updatedParsedBlog.sections)) {
          if (updatedParsedBlog.sections[sectionIdx]) {
            updatedParsedBlog.sections[sectionIdx] = {
              ...updatedParsedBlog.sections[sectionIdx],
              content: newContent
            };
          }
        } else if (typeof updatedParsedBlog.sections === 'object') {
          // If sections is an object with numbered keys
          const sectionKeys = Object.keys(updatedParsedBlog.sections);
          if (sectionKeys.length > sectionIdx) {
            const key = sectionKeys[sectionIdx];
            updatedParsedBlog.sections[key] = {
              ...updatedParsedBlog.sections[key],
              content: newContent
            };
          }
        }
        setParsedBlog(updatedParsedBlog);
      }

      toast.success("Section regenerated successfully!");
    } catch (error) {
      console.error("Error regenerating section:", error);
      toast.error("Failed to regenerate section.");
    } finally {
      // Close modal and reset states
      setIsRegenerating(false);
      setIsRegenerateModalOpen(false);
      setRegenerateContext(null);
    }
  };

  useEffect(() => {
    if (formData) {
      const newSelectedLocation = formData.location
        ? (typeof formData.location === 'object' ? formData.location : { label: formData.location })
        : null;
      setSelectedLocation(newSelectedLocation);

      let newLanguageState = null;
      if (formData.language) {
        newLanguageState = typeof formData.language === 'object' ? formData.language : { label: formData.language };
      } else if (formState.language === 'english') {
        newLanguageState = { label: 'english' };
      }
      setLanguage(newLanguageState);
    }
  }, [formData]); 


  useEffect(() => {
    if (sections && Array.isArray(sections)) {
      sections.forEach((section, idx) => {
        console.log(`Section ${idx}:`, {
          title: section.section_title || section.title,
          contentLength: section.content?.length || 0,
          lastUpdated: section.lastUpdated,
          contentPreview: (typeof section.content === 'string' 
            ? section.content.substring(0, 50) 
            : Array.isArray(section.content) 
              ? section.content.join(' ').substring(0, 50)
              : String(section.content || '').substring(0, 50)
          ) + '...'
        });
      });
    }
  }, [sections, updateTrigger]);

  // Additional effect to specifically track section content changes
  useEffect(() => {
    console.log('Sections array reference changed, length:', sections?.length || 0);
  }, [sections]);

  const handleSaveDraft = async () => {
    // Check if this is a newly created draft (no existing blogId)
    const hasExistingDraft = currentBlogId || blogId;
    
    // If it's a newly created draft, show modal to enter draft name (new only, no update option)
    if (!hasExistingDraft) {
      console.log("New blog - showing modal to enter draft name");
      setShowBlogDraftModal(true);
      return;
    }

    // Check if this is a Shopify blog that should trigger the name change modal
    // For now, we'll assume all blogs are local unless specified otherwise
    // You can modify this logic based on your specific requirements
    const blogPlatform = formData?.platform || 'Local';
    const isShopifyBlog = blogPlatform === 'Shopify';

    console.log(
      "Save as draft clicked - Blog platform:",
      blogPlatform,
      "Is Shopify:",
      isShopifyBlog
    );

    if (isShopifyBlog) {
      // For Shopify blogs, you might want to show a different modal or handle differently
      console.log("Shopify blog detected - handling differently");
      // For now, proceed with normal save
    }

    // For existing drafts, show the blog draft modal to allow update or create new
    console.log("Showing blog draft modal for existing draft");
    setShowBlogDraftModal(true);
  };

  const handleBlogDraftConfirm = async (draftChoice, newDraftTitle) => {
    console.log("Blog draft modal confirmed:", draftChoice, newDraftTitle);
    
    // Check if this is a new blog (no existing draft)
    const isNewBlog = !currentBlogId && !blogId;
    
    if (draftChoice === "update") {
      // Update existing draft
      await performSaveBlogDraft(null, true);
    } else if (draftChoice === "new") {
      // Create new draft
      console.log("Creating new blog draft with title:", newDraftTitle);
      
      if (isNewBlog) {
        // For new blogs, simply create with the provided name
        try {
          setIsSavingDraft(true);
          await performSaveBlogDraft(newDraftTitle, true);
          toast.success(`Blog draft "${newDraftTitle}" created successfully!`);
        } catch (error) {
          toast.error("Failed to create blog draft. Please try again.");
          console.error("Error creating blog draft:", error);
        } finally {
          setIsSavingDraft(false);
        }
      } else {
        // For existing blogs creating a new draft, use the complex logic
        // Update the blog title and clear the existing blog ID to force creation of new record
        setFormState(prev => ({ ...prev, about: newDraftTitle }));
        setCurrentBlogId(null); // Force creation of new record
        
        // Generate new unique identifiers for the new draft
        const newUid = `blog_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newAgentId = `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Update formData with new identifiers to ensure new record creation
        const updatedFormData = {
          ...formData,
          uid: newUid,
          session_id: newSessionId,
          agent_id: newAgentId
        };
        
        console.log("Updated formData with new identifiers for new draft:", updatedFormData);
        
        // Show loading state
        setIsSavingDraft(true);
        
        try {
          // Perform the save with the new title and force new record creation
          await performSaveBlogDraft(newDraftTitle, false, true, updatedFormData); // Pass updated form data
          toast.success(`New blog draft "${newDraftTitle}" created successfully!`);
        } catch (error) {
          toast.error("Failed to create new blog draft. Please try again.");
          console.error("Error creating new blog draft:", error);
        } finally {
          setIsSavingDraft(false);
        }
      }
    }
    
    // Close the modal
    setShowBlogDraftModal(false);
  };

  const performSaveBlogDraft = async (
    overrideTitle = null,
    showSuccessToast = true,
    forceNewRecord = false,
    updatedFormData = null
  ) => {
    setIsSavingDraft(true);
    
    try {
      // Use updated formData if provided, otherwise use current formData
      const dataToUse = updatedFormData || formData;
      
      // Generate new unique identifiers if forcing new record
      let newUid = dataToUse.uid || "";
      let newSessionId = dataToUse.session_id || "";
      let newAgentId = dataToUse.agent_id || "";
      
      if (forceNewRecord) {
        // Generate new unique identifiers to ensure new record creation
        newUid = `blog_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        newAgentId = `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        console.log("Force new record mode - generated new identifiers:", {
          newUid,
          newSessionId,
          newAgentId
        });
      }
      
      // Get the final blog data to save (this should include translated/edited content)
      const finalBlogData = getFinalBlogForSave();
      
      // Debug logging to verify we're saving the current state
      console.log('=== SAVE DEBUG ===');
      console.log('Current sections:', sections);
      console.log('Current htmlContent:', htmlContent ? htmlContent.substring(0, 100) + '...' : null);
      console.log('isHtmlFormat:', isHtmlFormat);
      console.log('parsedBlog:', parsedBlog ? 'exists' : 'null');
      console.log('finalBlogData length:', finalBlogData ? finalBlogData.length : 0);
      console.log('finalBlogData preview:', finalBlogData ? finalBlogData.substring(0, 200) : null);
      
      const result = await saveBlogDraftV2({
        name: overrideTitle || (formState.about || dataToUse?.topic || formData?.topic)?.slice(0, 40),
        tone: formState.tone,
        about: overrideTitle || formState.about || dataToUse?.topic || formData?.topic || "",
        word_limit: formState.word_limit,
        location: formState.location,
        language: formState.language,
        further_details: formState.further_details,
        status: 'draft',
        images: [],
        image_names: [],
        keyword_clusters: JSON.stringify(formState.semantic_clusters) || '',
        outlines: blogOutline ? JSON.stringify(blogOutline) : '',
        final_blog: finalBlogData, // Use the final blog data with current state
        session_id: newSessionId,
        agent_id: newAgentId,
        uid: newUid,
        // If forcing new record, don't include existing blog ID
        ...(forceNewRecord && { id: undefined, _id: undefined })
      });
      
      if (forceNewRecord) {
        // For new records, set the new ID
        setCurrentBlogId(result.blog.id || result.blog.why_id);
      } else {
        // For updates, keep the existing ID
        setCurrentBlogId(result.blog.id || result.blog.why_id);
      }
      
      if (showSuccessToast) {
        toast.success(forceNewRecord ? 'New blog draft created successfully!' : 'Blog draft updated successfully!');
      }
    } catch (err) {
      console.error('Failed to save blog draft:', err);
      let errorMessage = 'Unknown error';
      
      // Handle different error response formats
      if (err.response && err.response.data) {
        // Handle axios-style error responses
        const errorData = err.response.data;
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err.text === 'function') {
        try {
          const errorText = await err.text();
          const parsedError = JSON.parse(errorText);
          if (parsedError.errors && Array.isArray(parsedError.errors)) {
            errorMessage = parsedError.errors.join(', ');
          } else {
            errorMessage = errorText;
          }
        } catch (textErr) {
          errorMessage = 'Failed to parse API error response.';
        }
      } else {
        try {
          errorMessage = JSON.stringify(err);
        } catch (stringifyErr) {}
      }
      toast.error(errorMessage);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePreview = async (mode) => {
    const currentId = currentBlogId || blogId;
    if (!currentId) {
      toast.warning("Please save the blog as draft before previewing.");
      return;
    }
    try {
      navigate(`/skills/blog/blog-preview/${currentId}`, { 
        state: { 
          blog: finalBlog,
          textColor, 
          viewMode: mode || viewMode 
        } 
      });
    } catch (err) {
      toast.error("Failed to fetch blog for preview.");
    }
  };

  useEffect(() => {
    // If we have finalBlog data (from edit), use it directly without API call
    if (finalBlog && !hasInitialized) {
      setHasInitialized(true);
      setIsStreamingDone(true);
      setParsedBlog(finalBlog);
      setCurrentBlogId(blogId);
      return;
    }

    const payloadToUse = newPayload || payload;
    if (payloadToUse && !hasInitialized) {
      setHasInitialized(true);
      setJsonContent("");
      setHasFirstChunk(false);
      setIsStreamingDone(false);
      setParsedBlog(null);
      setIsStreaming(true);
      setStreamingStats({ startTime: null, charCount: 0, chunksReceived: 0 });
      let fullText = "";

      // Handle both old payload format and new parameter format
      if (newPayload) {
        // New format: individual parameters
        generateFullBlog(
          newPayload.topic,
          newPayload.tone,
          newPayload.wordCountTarget,
          newPayload.location,
          newPayload.language,
          newPayload.semanticClusters || [],
          newPayload.brandContext || "",
          newPayload.excludeTopics || "",
          newPayload.ctaMode || "",
          newPayload.ctaProductName || "",
          newPayload.ctaUrl || "",
          newPayload.faqCount || "",
          newPayload.topicMapJson,
          newPayload.blogType || "",
          newPayload.sessionId,
          newPayload.agentId,
          newPayload.uid,
          newPayload.query || newPayload.topic,
          newPayload.format || "html",
          (chunk) => {
            if (!hasFirstChunk) {
              setHasFirstChunk(true);
              setStreamingStats(prev => ({ ...prev, startTime: Date.now() }));
            }
            setJsonContent((prev) => prev + chunk);
            setStreamingStats(prev => ({ 
              ...prev, 
              charCount: prev.charCount + chunk.length,
              chunksReceived: prev.chunksReceived + 1
            }));
            fullText += chunk;
          }
        )
          .then(() => {
            setIsStreamingDone(true);
            setIsStreaming(false);
            try {
              const firstBrace = fullText.indexOf("{");
              const lastBrace = fullText.lastIndexOf("}");
              if (firstBrace !== -1 && lastBrace !== -1) {
                const jsonStr = fullText.substring(firstBrace, lastBrace + 1);
                setParsedBlog(JSON.parse(jsonStr));
                setParsingError(false);
              } else {
                setParsedBlog(null);
                setParsingError(true);
                setRawStreamedContent(fullText);
              }
            } catch (e) {
              setParsedBlog(null);
              setParsingError(true);
              setRawStreamedContent(fullText);
              setErrorMessage("Failed to parse JSON response");
            }
          })
          .catch((error) => {
            setIsStreaming(false);
            setIsStreamingDone(true);
            setParsedBlog(null);
            setHasError(true);
            setErrorMessage(error.message || "Network error");
            toast.error(
              "Failed to generate blog: " + (error.message || "Network error")
            );
          });
      } else if (payload) {
        // Old format: payload object (for backward compatibility)
        generateFullBlog(
          payload.additional_data?.blog_about || formData?.topic || formData?.about || "",
          payload.additional_data?.tone_of_voice || formData?.tone || "",
          payload.additional_data?.blog_length || formData?.word_limit || "",
          payload.additional_data?.location_focus || formData?.location || "",
          payload.additional_data?.language || formData?.language || "",
          payload.additional_data?.semantic_clusters || formData?.semantic_clusters || [],
          payload.additional_data?.brand_context || formData?.brand_context || "",
          formData?.exclude_topics || "",
          formData?.cta_mode || "",
          formData?.cta_product_name || "",
          formData?.cta_url || "",
          formData?.faq_count || "",
          typeof blogOutline === 'string' ? blogOutline : JSON.stringify(blogOutline || {}),
          formData?.blog_type || "",
          payload.additional_data?.session_id || formData?.session_id || "",
          payload.additional_data?.agent_id || formData?.agent_id || "",
          payload.additional_data?.uid || formData?.uid || "",
          payload.additional_data?.blog_about || formData?.topic || formData?.about || "",
          payload.additional_data?.format || "html",
          (chunk) => {
            if (!hasFirstChunk) {
              setHasFirstChunk(true);
              setStreamingStats(prev => ({ ...prev, startTime: Date.now() }));
            }
            setJsonContent((prev) => prev + chunk);
            setStreamingStats(prev => ({ 
              ...prev, 
              charCount: prev.charCount + chunk.length,
              chunksReceived: prev.chunksReceived + 1
            }));
            fullText += chunk;
          }
        )
          .then(() => {
            setIsStreamingDone(true);
            setIsStreaming(false);
            try {
              const firstBrace = fullText.indexOf("{");
              const lastBrace = fullText.lastIndexOf("}");
              if (firstBrace !== -1 && lastBrace !== -1) {
                const jsonStr = fullText.substring(firstBrace, lastBrace + 1);
                setParsedBlog(JSON.parse(jsonStr));
                setParsingError(false);
              } else {
                setParsedBlog(null);
                setParsingError(true);
                setRawStreamedContent(fullText);
              }
            } catch (e) {
              setParsedBlog(null);
              setParsingError(true);
              setRawStreamedContent(fullText);
              setErrorMessage("Failed to parse JSON response");
            }
          })
          .catch((error) => {
            setIsStreaming(false);
            setIsStreamingDone(true);
            setParsedBlog(null);
            setHasError(true);
            setErrorMessage(error.message || "Network error");
            toast.error(
              "Failed to generate blog: " + (error.message || "Network error")
            );
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, newPayload, hasInitialized, finalBlog, blogId]);
  

  useEffect(() => {
    if (isStreamingDone && parsedBlog) {
      // Check if format is HTML and blog.content_html exists
      const format = parsedBlog?.metadata?.format || newPayload?.format || payload?.additional_data?.format || formData?.format || "html";
      if (format === "html" && parsedBlog?.blog?.content_html) {
        setIsHtmlFormat(true);
        setHtmlContent(parsedBlog.blog.content_html);
        setSections(null);
      } else if (format === "markdown" && parsedBlog?.blog?.content_markdown?.sections) {
        // Extract sections from content_markdown.sections array
        setIsHtmlFormat(false);
        setHtmlContent(null);
        const markdownSections = parsedBlog.blog.content_markdown.sections;
        setSections(markdownSections);
      } else {
        // Fallback to existing sections structure
        setIsHtmlFormat(false);
        setHtmlContent(null);
        const cleanedBlog = normalizeBlogData(parsedBlog);
        const newSections = flattenSections(cleanedBlog.sections);
        setSections(newSections);
      }
    }
  }, [isStreamingDone, parsedBlog, newPayload, payload, formData]);

  
  useEffect(() => {
    if (finalBlog && !sections && !htmlContent) {
      // Check format for finalBlog
      const format = finalBlog?.metadata?.format || formData?.format || "html";
      if (format === "html" && finalBlog?.blog?.content_html) {
        setIsHtmlFormat(true);
        setHtmlContent(finalBlog.blog.content_html);
        setSections(null);
      } else if (format === "markdown" && finalBlog?.blog?.content_markdown?.sections) {
        // Extract sections from content_markdown.sections array
        setIsHtmlFormat(false);
        setHtmlContent(null);
        const markdownSections = finalBlog.blog.content_markdown.sections;
        setSections(markdownSections);
      } else {
        setIsHtmlFormat(false);
        setHtmlContent(null);
        const cleanedBlog = normalizeBlogData(finalBlog);
        const newSections = flattenSections(cleanedBlog.sections);
        setSections(newSections);
      }
    }
  }, [finalBlog, sections, htmlContent, formData]);

  function getFinalBlogForSave() {
    // Always prioritize current state (translated/edited content) over original parsedBlog
    // Check current state first to ensure we save the latest content
    
    console.log('getFinalBlogForSave called:', {
      hasSections: sections && sections.length > 0,
      sectionsLength: sections?.length || 0,
      hasHtmlContent: !!htmlContent,
      htmlContentLength: htmlContent?.length || 0,
      isHtmlFormat,
      hasParsedBlog: !!parsedBlog
    });
    
    // PRIORITY 1: If we have sections (translated/edited), use them - this is the most important
    if (sections && sections.length > 0) {
      console.log('Saving with sections format, sections count:', sections.length);
      console.log('First section preview:', sections[0]?.content?.substring?.(0, 100) || sections[0]?.content);
      
      // Save sections format - use current sections (may be translated/edited)
      if (parsedBlog) {
        // Preserve metadata but use current sections - this ensures translated content is saved
        // IMPORTANT: Create a new object and explicitly set sections to avoid any merging issues
        const blogToSave = { 
          ...parsedBlog,
          // Remove any old sections from nested structures
          blog: parsedBlog.blog ? {
            ...parsedBlog.blog,
            // Remove content_markdown.sections if it exists to avoid confusion
            content_markdown: parsedBlog.blog.content_markdown ? {
              ...parsedBlog.blog.content_markdown,
              sections: undefined // Remove old sections
            } : undefined
          } : undefined,
          // CRITICAL: Use current translated/edited sections, completely replace any old sections
          sections: sections
        };
        // Explicitly remove any other section references
        delete blogToSave.blog?.content_markdown?.sections;
        
        console.log('Saving with parsedBlog metadata, using current sections');
        console.log('BlogToSave sections count:', blogToSave.sections?.length);
        return JSON.stringify(blogToSave);
      } else {
        // If no parsedBlog, use current sections with metadata from finalBlog if available
        const blogToSave = { 
          sections: sections, // Use current translated/edited sections
          id: currentBlogId || blogId,
          metadata: finalBlog?.metadata || { format: "markdown" },
          // Add other necessary fields if available
          ...(finalBlog ? { ...finalBlog, sections: undefined } : {}) // Spread finalBlog but override sections
        };
        console.log('Saving sections without parsedBlog');
        return JSON.stringify(blogToSave);
      }
    }
    
    // PRIORITY 2: If we have HTML content (translated/edited), use it
    if (isHtmlFormat && htmlContent) {
      console.log('Saving with HTML format, htmlContent length:', htmlContent.length);
      // Save HTML format blog - use current htmlContent (may be translated/edited)
      if (parsedBlog) {
        // Preserve metadata but use current htmlContent
        const blogToSave = { 
          ...parsedBlog, 
          blog: { 
            ...parsedBlog.blog, 
            content_html: htmlContent // Use current translated/edited HTML content
          } 
        };
        console.log('Saving HTML with parsedBlog metadata, using current htmlContent');
        return JSON.stringify(blogToSave);
      } else {
        // If no parsedBlog, create structure with current htmlContent
        const blogToSave = {
          blog: {
            content_html: htmlContent
          },
          metadata: {
            format: "html"
          },
          id: currentBlogId || blogId
        };
        console.log('Saving HTML without parsedBlog');
        return JSON.stringify(blogToSave);
      }
    }
    
    // PRIORITY 3: Fallback to parsedBlog if no current state (should rarely happen)
    if (parsedBlog) {
      console.log('Falling back to parsedBlog (no current state found)');
      return JSON.stringify(parsedBlog);
    }
    
    console.warn('getFinalBlogForSave: No content to save!');
    return null;
  }

  if (!payload && !formData && !finalBlog && !sections) {
    return (
      <>
        <ToastContainer />
        <div style={{ color: "red", padding: 40 }}>No blog data found. Please generate a blog first.</div>
      </>
    );
  }


  if (isStreaming || (!isStreamingDone && !parsedBlog && !sections)) {
    return (
      <div style={{ height: '100vh', overflowY: 'auto' }}>
        <ToastContainer />
        <div className="breadcrumb-container mx-3">
          <div className="breadcrumb-back">
            <span className="back-text" onClick={() => navigate(-1)}>&lt; Back</span>
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/">Home</a>
              </li>
              <span className="breadcrumb-separator"> &gt; </span>
              <li className="breadcrumb-item ">
                <a href="/skills">Skills</a>
              </li>
              <span className="breadcrumb-separator"> &gt; </span>
              <li className="breadcrumb-item ">
                <a href="/skills/blog">Blog templates</a>
              </li>
              <span className="breadcrumb-separator"> &gt; </span>
              <li className="breadcrumb-item active">
                <span>Generate New Blog</span>
              </li>
            </ol>
          </nav>
        </div>
        <div className="d-flex justify-content-between mb-3">
          <div className="title-label">Generate New Blog</div>
          <div className="d-flex gap-2">
            <div>
              <button className="btn bgprimary rounded" onClick={handleSaveDraft} disabled={isSavingDraft}>Save as draft</button>
            </div>
            <div><button className="btn btn-dark rounded" >Push to shop</button></div>
            <div><button className="btn bgprimary" onClick={() => handleShare(blogId)}>Share</button></div>
          </div>
        </div>
        <div className="row">
          {/* Left: Form display - This is the fallback view during streaming */}
          <div className="col-md-4">
            <h3 className="form-title">Form Data Summary</h3>
            <div className="info-display">
              <div className="info-group">
                <label>What is this blog about? *</label>
                <div className="info-text">{formState.about}</div>
              </div>
              <div className="info-group">
                <label>What should be the tone of voice?</label>
                <div className="info-text">{formState.tone}</div>
              </div>
              <div className="info-group">
                <label>How long do you want the blog to be?</label>
                <div className="info-text">{formState.word_limit}</div>
              </div>
              {selectedLocation && (
                <div className="info-group">
                  <label>Location focus</label>
                  <div className="info-text">{selectedLocation.label}</div>
                </div>
              )}
              {language && (
                <div className="info-group">
                  <label>Language</label>
                  <div className="info-text">{language.label}</div>
                </div>
              )}
              {formState.further_details && (
                <div className="info-group">
                  <label>Further details</label>
                  <div className="info-text">{formState.further_details}</div>
                </div>
              )}
            </div>
          </div>
          {/* Right: Blog content will be displayed here */}
          <div className="col-md-8 border-start" style={{ position: 'relative' }}>
            {!hasFirstChunk ? (
               <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: 400, 
                color: '#666', 
                fontSize: '16px',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <div style={{ 
                  width: '60px', 
                  height: '60px', 
                  border: '4px solid #f3f3f3', 
                  borderTop: '4px solid #E36250', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite'
                }}></div>
                <div>Generating your blog content...</div>
              </div>
            ) : (
              <div style={{ padding: '20px', minHeight: 400 }}>
                
                {/* Display streaming content */}
                <div style={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '8px', 
                  padding: '20px',
                  minHeight: '300px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: '#495057',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}>
                  {jsonContent ? (
                    <div>
                      <div 
                        ref={streamingContentRef}
                        style={{ 
                          backgroundColor: '#f8f9fa', 
                          padding: '16px', 
                          borderRadius: '6px',
                          border: '1px solid #e9ecef',
                          maxHeight: '400px',
                          overflowY: 'auto'
                        }}
                      >
                        <div style={{ 
                          fontFamily: 'monospace', 
                          fontSize: '12px', 
                          lineHeight: '1.5',
                          color: '#495057',
                          position: 'relative'
                        }}>
                          {formatStreamingContent(jsonContent)}
                          {isStreaming && (
                            <span style={{ 
                              display: 'inline-block',
                              width: '2px',
                              height: '16px',
                              backgroundColor: '#E36250',
                              marginLeft: '2px',
                              opacity: cursorVisible ? 1 : 0,
                              transition: 'opacity 0.1s ease'
                            }}></span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '200px',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}>
                      <div className="loader-spinner" style={{ marginRight: '12px' }}></div>
                      Waiting for content to start streaming...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if ((isStreamingDone && parsedBlog) || (sections && sections.length > 0) || (isHtmlFormat && htmlContent)) {
    const displayBlogId = parsedBlog?.id || parsedBlog?._id || currentBlogId || blogId;
    return (
      <>
        {/* The main container for the playground view when data is ready */}
        <div >
        <ToastContainer />
        <div className="breadcrumb-container mx-3">
        <div className="breadcrumb-back">
          <span className="back-text" onClick={() => navigate(-1)}>&lt; Back</span>
        </div><nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <a href="/">Home</a></li>
            <span class="breadcrumb-separator"> &gt; </span>
            <li class="breadcrumb-item ">
              <a href="/skills">Skills</a></li>
            <span class="breadcrumb-separator"> &gt; </span>
            <li class="breadcrumb-item ">
              <a href="/skills/blog">Blog templates</a></li>
            <span class="breadcrumb-separator"> &gt; </span>
            <li class="breadcrumb-item active">
              <span>Generate New Blog</span></li></ol>
        </nav></div>
        <div class="d-flex justify-content-between mb-3">
                        <div class="title-label">Generate New Blog</div>
                        <div class="d-flex gap-2">
                        <div>
                            <button class="btn bgprimary rounded" onClick={handleSaveDraft} disabled={isSavingDraft || (!sections || !sections.length) && !htmlContent}>
                            {isSavingDraft ? 'Saving...' : 'Save as draft'}</button>
                            </div>
                            <div><button class="btn btn-dark rounded" >Push to shop</button>
                          </div>
                          <div><button class="btn bgprimary" onClick={() => (displayBlogId) ? handleShare(displayBlogId) : toast.info('Save draft first to enable sharing')}>
                          <i className="fas fa-link"></i> Share</button>
                          </div>
                          
                            </div>
                            </div>

        {/* Two-column layout for sidebar and playground content */}
        <div className="row">
         <div className="col-md-4">
          <GenerateBlogLeft
            about={formState.about}
            tone={formState.tone}
            word_limit={formState.word_limit}
            keywords={Array.isArray(formState.semantic_clusters) ? formState.semantic_clusters.join(', ') : ''}
            further_details={formState.further_details}
            brand_context={formState.brand_context}
            location={formState.location}
            language={formState.language}
            onChange={handleChange}
            onOptimize={() => setShowOptimizeModal(true)}
            onTranslate={onTranslate}
            isTranslating={isTranslating}
            loadLocationOptions={loadLocationOptions}
            onLocationChange={(selectedOption) => {
              setFormState(prevForm => ({...prevForm, location: selectedOption ? selectedOption.value : ""}));
            }}
          />
          </div>
          {/* Right: Blog Figma Design */}
          <div className="col-md-8 border-start" style={{ position: 'relative' }}>
          {/* Full-screen translation loader overlay */}
          {isTranslating && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                gap: '20px'
              }}
            >
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #E36250',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 500,
                  color: '#333',
                  textAlign: 'center'
                }}
              >
                Translating your blog content...
              </div>
              <div
                style={{
                  fontSize: '14px',
                  color: '#666',
                  textAlign: 'center',
                  maxWidth: '400px'
                }}
              >
                Please wait while we translate your content. This may take a few moments.
              </div>
            </div>
          )}
          <div >
          
            {/* Preview/Tab Buttons */}
            {/* TODO: Move styles for these buttons to CSS for better consistency */}
            <div className="d-flex gap-4 align-items-center" >
            <h5 style={{ fontWeight: "500", fontSize: "16px" }}>
              Blog Playground
            </h5>
          <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">

            <h6 className="mt-2 mx-1">Preview</h6>
            <div className="d-flex justify-content-center align-items-center">
  <button className="btn" onClick={() => handlePreview("desktop")}>
    <BiLaptop size={20} />
  </button> |
  <button className="btn" onClick={() => handlePreview("mobile")}>
    <BiMobile size={20} />
  </button>
</div>

      </div>
        </div>
            {/* <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <span style={{ fontWeight: 600, fontSize: 18 }}>Blog Playground</span>
              <button
                className="preview-button" 
                onClick={handlePreview}
                style={{ border: '1px solid #E36250', borderRadius: 6, background: 'transparent', padding: '4px 12px', fontSize: 14, color: '#E36250', cursor: 'pointer' }}
              >
                Preview
              </button>
              <button 
                className={`preview-device-button ${viewMode === 'desktop' ? 'active' : ''}`}
                onClick={() => setViewMode('desktop')}
                style={{
                  border: viewMode === 'desktop' ? '1px solid #E36250' : '1px solid #ccc',
                  borderRadius: 6, 
                  background: viewMode === 'desktop' ? '#E36250' : '#fff',
                  color: viewMode === 'desktop' ? '#fff' : '#333',
                  padding: '4px 12px', 
                  fontSize: 14, 
                  cursor: 'pointer' 
                }}
              >
                <i className="fas fa-desktop"></i>
              </button>
              <button 
                className={`preview-device-button ${viewMode === 'mobile' ? 'active' : ''}`}
                onClick={() => setViewMode('mobile')}
                style={{
                  border: viewMode === 'mobile' ? '1px solid #E36250' : '1px solid #ccc',
                  borderRadius: 6, 
                  background: viewMode === 'mobile' ? '#E36250' : '#fff',
                  color: viewMode === 'mobile' ? '#fff' : '#333',
                  padding: '4px 12px', 
                  fontSize: 14, 
                  cursor: 'pointer' 
                }}
              >
                <i className="fas fa-mobile-alt"></i>
              </button>
            </div> */}
            
            {/* Blog Sections (Eyebrow, Title, Intro, Tags, Content, etc.) */}
            <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {isHtmlFormat && htmlContent ? (
                <HtmlContentRenderer
                  htmlContent={htmlContent}
                  onContentChange={(newContent) => {
                    // Update the HTML content state
                    setHtmlContent(newContent);
                    // Also update parsedBlog if it exists
                    if (parsedBlog) {
                      setParsedBlog({
                        ...parsedBlog,
                        blog: {
                          ...parsedBlog.blog,
                          content_html: newContent
                        }
                      });
                    }
                  }}
                />
              ) : (sections && sections.length > 0 ? sections.map((section, idx) => (
                // <div key={`section-${idx}-${updateTrigger}-${section.lastUpdated || 0}-${section.content?.substring(0, 20) || 'empty'}`} className="outline-section-item mb-4" >
                <div key={`section-${idx}-${updateTrigger}-${section.lastUpdated ?? 0}-${
                      (Array.isArray(section.content)
                        ? section.content.join(' ')
                        : (section.content ?? '')
                      ).slice(0, 20) || 'empty'
                    }`}
                    className="outline-section-item"
                    style={{padding: '20px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}
                  >
                  <div className="section-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                  <div className="outline-title" style={{flex: 1, minWidth: 0, marginRight: '16px'}}>
                    <h4 style={{
                      margin: 0, 
                      color: '#1f2937', 
                      fontSize: '14px', 
                      fontWeight: 600,
                      wordWrap: 'break-word',
                      lineHeight: '1.3'
                    }}>
                      {section.section_title || section.title || `Section ${idx + 1}`}
                    </h4>
                  </div>
                    
                    <div className="button-group" style={{display: 'flex', gap: '8px', flexWrap: 'nowrap', flexShrink: 0}}>
                      <button 
                        className="btn btn-outline-secondary" 
                        style={{borderRadius: '6px', padding: '6px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}
                        onClick={() => {
                          // Convert content to text format for regeneration
                          let contentText = '';
                          if (Array.isArray(section.content)) {
                            if (section.section_title && section.section_title.toLowerCase().includes('frequently asked questions')) {
                              contentText = section.content.map(faq => `Q: ${faq.question}\nA: ${faq.answer}`).join('\n\n');
                            } else {
                              contentText = section.content.join('\n');
                            }
                          } else if (typeof section.content === 'string') {
                            // If content is already a string, use it as is
                            contentText = section.content;
                          } else {
                            contentText = String(section.content || '');
                          }
                          handleOpenRegenerateModal(idx, contentText);
                        }}
                      >
                        <BiRedo size={16} /> Regenerate text
                      </button>
                      <button 
                        className="btn btn-outline-secondary" 
                        style={{borderRadius: '6px', padding: '6px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}
                        onClick={() => {
                          setEditingSectionIdx(idx);
                          // Convert content to editable text format
                          let editText = '';
                          if (Array.isArray(section.content)) {
                            if (section.section_title && section.section_title.toLowerCase().includes('frequently asked questions')) {
                              // Convert FAQ objects to Q&A format
                              editText = section.content.map(faq => `Q: ${faq.question}\nA: ${faq.answer}`).join('\n\n');
                            } else {
                              editText = section.content.join('\n');
                            }
                          } else {
                            editText = String(section.content || '');
                          }
                          setEditSectionText(editText);
                        }}
                      >
                        <BiEdit size={16} /> Edit text
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        style={{borderRadius: '6px', padding: '6px 12px', fontSize: '14px'}}
                        onClick={() => handleDeleteSection(idx)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {editingSectionIdx === idx ? (
                    <div style={{marginBottom: 8}}>
                      <textarea
                        value={editSectionText}
                        onChange={e => setEditSectionText(e.target.value)}
                        className="form-control"
                        rows={4}
                        style={{borderRadius: '8px', border: '1px solid #d1d5db', padding: '12px'}}
                      />
                      <div style={{marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <button className="btn bgprimary" style={{borderRadius: '6px', padding: '8px 16px'}} onClick={() => {
                          const updated = [...sections];
                          // Parse edited text back to appropriate format
                          let parsedContent = editSectionText;
                          
                          if (section.section_title && section.section_title.toLowerCase().includes('frequently asked questions')) {
                            // Parse different FAQ formats back to FAQ objects
                            let parsedContent;
                            
                            // Try Q: A: format first
                            if (editSectionText.includes('Q:') && editSectionText.includes('A:')) {
                              const qaPairs = editSectionText.split(/\n\s*\n/).filter(pair => pair.trim());
                              parsedContent = qaPairs.map(pair => {
                                const lines = pair.split('\n').filter(line => line.trim());
                                const question = lines.find(line => line.startsWith('Q:'))?.replace('Q:', '').trim();
                                const answer = lines.find(line => line.startsWith('A:'))?.replace('A:', '').trim();
                                return { question, answer };
                              }).filter(qa => qa.question && qa.answer);
                            }
                            // Try bullet point format with markdown
                            else if (editSectionText.includes('- **') && editSectionText.includes('**:')) {
                              const lines = editSectionText.split('\n').filter(line => line.trim());
                              parsedContent = lines.map(line => {
                                if (line.startsWith('- **') && line.includes('**:')) {
                                  const match = line.match(/^-\s*\*\*(.*?)\*\*:\s*(.*)/);
                                  if (match) {
                                    const [, question, answer] = match;
                                    return { question, answer };
                                  }
                                }
                                return null;
                              }).filter(qa => qa && qa.question && qa.answer);
                            }
                            // If no specific format, keep as string
                            else {
                              parsedContent = editSectionText;
                            }
                          } else if (section.section_title && section.section_title.toLowerCase().includes('myths')) {
                            // Keep as string for myths sections
                            parsedContent = editSectionText;
                          } else if (Array.isArray(section.content)) {
                            // For other array content, split by newlines
                            parsedContent = editSectionText.split('\n').filter(line => line.trim());
                          }
                          
                          updated[idx].content = parsedContent;
                          updated[idx].lastUpdated = Date.now(); // Mark as updated
                          setSections(updated);
                          
                          // Update parsedBlog if it exists to keep it in sync with edited sections
                          if (parsedBlog) {
                            setParsedBlog(prev => ({
                              ...prev,
                              sections: updated // Update with edited sections
                            }));
                          }
                          
                          setEditingSectionIdx(null);
                          setEditSectionText("");
                        }}>Save</button>
                        <button className="btn btn-outline-secondary" style={{borderRadius: '6px', padding: '8px 16px'}} onClick={() => {
                          setEditingSectionIdx(null);
                          setEditSectionText("");
                        }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="section-content" style={{padding: '16px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #f3f4f6'}}>{
                      renderSectionContent(section.content, section.section_title)
                    }</div>
                  )}
                  {section.suggested_image_or_infographic && (
                    <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fde68a', color: '#92400e', fontStyle: 'italic', fontSize: '14px' }}>
                      💡 <strong>Suggested Visual:</strong> {section.suggested_image_or_infographic}
                    </div>
                  )}
                  {section.keywords_integrated && Array.isArray(section.keywords_integrated) && (
                    <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#dbeafe', borderRadius: '6px', border: '1px solid #93c5fd' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
                        Related Keywords:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {section.keywords_integrated.map((keyword, idx) => (
                          <span key={idx} style={{ 
                            backgroundColor: '#3b82f6', 
                            color: 'white', 
                            padding: '4px 8px', 
                            borderRadius: '12px', 
                            fontSize: '12px' 
                          }}>
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {section.word_count && (
                    <div style={{ marginTop: 12, textAlign: 'right', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                      {section.word_count} words
                    </div>
                  )}
                </div>
              )) : null)}
            </div>


          </div>
          </div>
          <BlogOptimizeModal
            show={showOptimizeModal}
            onHide={() => setShowOptimizeModal(false)}
            onStartOptimizing={() => {
              setShowOptimizeModal(false);
              handleOptimize();
            }}
            onEditManually={() => {
              setShowOptimizeModal(false);
            }}
          />

          <BlogDraftModal
            show={showBlogDraftModal}
            onHide={() => setShowBlogDraftModal(false)}
            onConfirm={handleBlogDraftConfirm}
            isChecking={isSavingDraft}
            newOnly={!currentBlogId && !blogId}
          />
        </div>
    </div>
    <RegenerateModal
      isOpen={isRegenerateModalOpen}
      onClose={() => setIsRegenerateModalOpen(false)}
      onSubmit={handleRegenerateSubmit}
      isLoading={isRegenerating}
    />
  </>
  );
  }

  // Show error state with retry option
  if (hasError || parsingError) {
    return (
      <>
        <ToastContainer />
        <div style={{ height: '100vh', overflowY: 'auto' }}>
          <div className="breadcrumb-container mx-3">
            <div className="breadcrumb-back">
              <span className="back-text" onClick={() => navigate(-1)}>&lt; Back</span>
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <span>Blog Generation</span>
                </li>
              </ol>
            </nav>
          </div>
          
          <div className="container-fluid" style={{ padding: '20px' }}>
            <div className="row justify-content-center">
              <div className="col-12 col-lg-10">
                <div className="card">
                  <div className="card-header bg-danger text-white">
                    <h4 className="mb-0">
                      {parsingError ? "Parsing Error" : "Generation Error"}
                    </h4>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-warning" role="alert">
                      <h5 className="alert-heading">
                        {parsingError ? "Failed to Parse Response" : "Blog Generation Failed"}
                      </h5>
                      <p>
                        {parsingError 
                          ? "The blog was generated but we couldn't parse the JSON response. The raw content is shown below."
                          : errorMessage || "An error occurred while generating the blog."
                        }
                      </p>
                      <hr />
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-primary" 
                          onClick={handleRetry}
                        >
                          <i className="fas fa-redo me-2"></i>
                          Retry Generation
                        </button>
                        <button 
                          className="btn btn-secondary" 
                          onClick={() => navigate(-1)}
                        >
                          <i className="fas fa-arrow-left me-2"></i>
                          Go Back
                        </button>
                      </div>
                    </div>
                    
                    {parsingError && rawStreamedContent && (
                      <div className="mt-4">
                        <h6>Raw Response Content:</h6>
                        <div 
                          style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {rawStreamedContent}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show message when no payload is available
  return (
    <>
      <ToastContainer />
      <div style={{ height: '100vh', overflowY: 'auto' }}>
        <div className="breadcrumb-container mx-3">
          <div className="breadcrumb-back">
            <span className="back-text" onClick={() => navigate(-1)}>&lt; Back</span>
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <span>Blog Generation</span>
              </li>
            </ol>
          </nav>
        </div>
        
        <div className="container-fluid" style={{ padding: '20px' }}>
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
              <div className="card">
                <div className="card-body text-center">
                  <div className="mb-4">
                    <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="card-title">No Blog Data Found</h4>
                  <p className="card-text text-muted">
                    Please generate a blog first to view the playground.
                  </p>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => navigate(-1)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPlayground;
